import {textareaWithVariables_isPillElement} from '../pill/is-element'
import {textareaWithVariables_isSpacer} from '../pill/is-spacer'


// Editor keyboard handling that makes the zero-width edge spacers
// TRANSPARENT to horizontal navigation and deletion. Without this the
// spacer (a real text node, hence a caret stop) forces the user to press
// arrow / Backspace twice to move past or delete a pill at a line edge.
//
//  - ArrowRight / ArrowLeft cross a pill (and its flanking spacers) in a
//    single press, landing the caret INSIDE the spacer on the far side so
//    it renders at the small font height.
//  - Backspace / Delete reposition the caret tight against the pill and
//    then let the NATIVE delete run, so the pill is removed atomically and
//    stays on the browser's undo stack (Ctrl+Z restores it) — without
//    `document.execCommand` (banned). Stray spacers left behind are cleaned
//    by the post-delete normalise in `onInput`.
//
// Up/Down are left to the browser — the spacers give the caret the text
// position it needs for native vertical navigation to work.


// The pill the collapsed caret sits immediately BEFORE, looking across an
// optional zero-width spacer. null when the next thing isn't a pill.
function pillToRight(node: Node, offset: number): HTMLElement | null {
    let a: Node | null
    if (textareaWithVariables_isSpacer(node))      a = node.nextSibling
    else if (node.nodeType === Node.TEXT_NODE)     a = offset >= (node.textContent || '').length ? node.nextSibling : null
    else                                           a = node.childNodes[offset] || null
    if (a && textareaWithVariables_isSpacer(a)) a = a.nextSibling
    return a && textareaWithVariables_isPillElement(a) ? (a as HTMLElement) : null
}

// The pill the collapsed caret sits immediately AFTER (across a spacer).
function pillToLeft(node: Node, offset: number): HTMLElement | null {
    let b: Node | null
    if (textareaWithVariables_isSpacer(node))      b = node.previousSibling
    else if (node.nodeType === Node.TEXT_NODE)     b = offset <= 0 ? node.previousSibling : null
    else                                           b = offset > 0 ? node.childNodes[offset - 1] : null
    if (b && textareaWithVariables_isSpacer(b)) b = b.previousSibling
    return b && textareaWithVariables_isPillElement(b) ? (b as HTMLElement) : null
}

function setCaret(node: Node, offset: number): void {
    const sel = window.getSelection()
    if (!sel) return
    const r = document.createRange()
    r.setStart(node, offset)
    r.collapse(true)
    sel.removeAllRanges()
    sel.addRange(r)
}

function setCaretAfter(node: Node): void {
    const r = document.createRange()
    r.setStartAfter(node)
    r.collapse(true)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(r)
}

function setCaretBefore(node: Node): void {
    const r = document.createRange()
    r.setStartBefore(node)
    r.collapse(true)
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(r)
}

// Land the caret on the far (right) side of a crossed pill — inside the
// trailing spacer (small caret) or at the start of following text.
function caretPastPillRight(pill: HTMLElement): void {
    const next = pill.nextSibling
    if (next && textareaWithVariables_isSpacer(next))   setCaret(next, (next.textContent || '').length)
    else if (next && next.nodeType === Node.TEXT_NODE)  setCaret(next, 0)
    else                                                setCaretAfter(pill)
}

function caretPastPillLeft(pill: HTMLElement): void {
    const prev = pill.previousSibling
    if (prev && textareaWithVariables_isSpacer(prev))   setCaret(prev, 0)
    else if (prev && prev.nodeType === Node.TEXT_NODE)  setCaret(prev, (prev.textContent || '').length)
    else                                                setCaretBefore(pill)
}


// Returns true if the event represents a delete that removed a pill via
// native deletion (so the caller knows to run the post-delete normalise).
export function textareaWithVariables_handleEditorKeyDown(e: KeyboardEvent, editor: HTMLElement): void {
    // Modifier combos (word-jump, shortcuts), Shift+Arrow selection and IME
    // composition all stay native.
    if (e.isComposing || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return

    const key = e.key
    if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Backspace' && key !== 'Delete') return

    const sel = window.getSelection()
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return
    const r = sel.getRangeAt(0)
    const node = r.startContainer
    if (!editor.contains(node)) return
    const offset = r.startOffset

    switch (key) {
        case 'ArrowRight': {
            const pill = pillToRight(node, offset)
            if (pill) { e.preventDefault(); caretPastPillRight(pill) }
            break
        }
        case 'ArrowLeft': {
            const pill = pillToLeft(node, offset)
            if (pill) { e.preventDefault(); caretPastPillLeft(pill) }
            break
        }
        case 'Backspace': {
            // Move tight against the pill; native Backspace then deletes it
            // (undoable). NOT preventing default is intentional.
            const pill = pillToLeft(node, offset)
            if (pill) setCaretAfter(pill)
            break
        }
        case 'Delete': {
            const pill = pillToRight(node, offset)
            if (pill) setCaretBefore(pill)
            break
        }
    }
}
